import type { WorkflowFilters, RunFilters, CheckpointFilters } from "@/types/workflow.types";
import type {
  WorkflowReferenceOptionsParams,
  WorkflowReferenceResolveRef,
} from "@/types/workflow-reference.types";

function normalizeReferenceOptionsKey(params: WorkflowReferenceOptionsParams) {
  return {
    kind: params.kind,
    action: params.action ?? "read",
    nodeType: params.nodeType ?? "",
    fieldKey: params.fieldKey ?? "",
    q: params.q ?? "",
    limit: params.limit ?? 30,
  };
}

function normalizeReferenceResolveKey(refs: WorkflowReferenceResolveRef[]) {
  return refs
    .map((ref) => ({
      kind: ref.kind,
      action: ref.action ?? "read",
      nodeType: ref.nodeType ?? "",
      fieldKey: ref.fieldKey ?? "",
      ids: [...new Set(ref.ids)].filter(Boolean).sort(),
    }))
    .sort((a, b) =>
      [a.kind, a.action, a.nodeType, a.fieldKey]
        .join(":")
        .localeCompare([b.kind, b.action, b.nodeType, b.fieldKey].join(":")),
    );
}

/**
 * Query key factory for workflows
 */
export const workflowKeys = {
  all: ["workflows"] as const,
  lists: () => [...workflowKeys.all, "list"] as const,
  list: (filters?: WorkflowFilters) => [...workflowKeys.lists(), filters] as const,
  details: () => [...workflowKeys.all, "detail"] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
  steps: (workflowId: string) => [...workflowKeys.detail(workflowId), "steps"] as const,
  runs: (workflowId: string, filters?: RunFilters) =>
    [...workflowKeys.detail(workflowId), "runs", filters] as const,
  run: (runId: string) => [...workflowKeys.all, "run", runId] as const,
  runSteps: (runId: string) => [...workflowKeys.run(runId), "steps"] as const,
  runEvents: (runId: string, filters?: { sinceId?: string; limit?: number }) =>
    [...workflowKeys.run(runId), "events", filters] as const,
  runLiveState: (runId: string) => [...workflowKeys.run(runId), "live-state"] as const,
  stats: (workflowId: string) => [...workflowKeys.detail(workflowId), "stats"] as const,
  webhooks: (workflowId: string) => [...workflowKeys.detail(workflowId), "webhooks"] as const,
  mcpTools: () => [...workflowKeys.all, "mcp-tools"] as const,
  nodeTypes: () => [...workflowKeys.all, "node-types"] as const,
  nodeType: (type: string) => [...workflowKeys.nodeTypes(), type] as const,
  referenceOptions: (params: WorkflowReferenceOptionsParams) =>
    [...workflowKeys.all, "reference-options", normalizeReferenceOptionsKey(params)] as const,
  referenceResolve: (refs: WorkflowReferenceResolveRef[]) =>
    [...workflowKeys.all, "reference-resolve", normalizeReferenceResolveKey(refs)] as const,
  // Checkpoint keys
  checkpoints: () => [...workflowKeys.all, "checkpoints"] as const,
  checkpointList: (filters?: CheckpointFilters) =>
    [...workflowKeys.checkpoints(), "list", filters] as const,
  checkpointDetail: (id: string) => [...workflowKeys.checkpoints(), "detail", id] as const,
  runCheckpoints: (runId: string) => [...workflowKeys.run(runId), "checkpoints"] as const,
};
